import { useContext, useState } from 'react';
import useSWRInfinite from 'swr/infinite';
import { cachedApiFetcher } from 'lib/swr';
import CommunityGrid from 'components/community/gridV2/CommunityGrid';
import { CommunityGridItem } from 'types/community';
import { gridItemHandler } from './CommunityView';
import styles from './CommunityView.module.css';
import { IsMobileContext } from 'contexts/IsMobile';

function getCommunityFeaturedUrl({ page = 0, limit = 4 } = {}) {
  const params: Record<string, string> = {
    page: page.toString(),
    limit: limit.toString(),
  };

  const queryString = new URLSearchParams(params).toString();
  const endpoint = '/api/community-posts/featured';
  const res = `${endpoint}?${queryString}`;
  return res;
}

/**
 * Main view that gets exported. We need two views to handle different page sizes for mobile vs desktop
 */
export default function CommunityView() {
  const { isAppMounted, isMobile } = useContext(IsMobileContext);
  if (!isAppMounted) {
    return null;
  }
  return <CommunityFeaturedView pageSize={isMobile ? 4 : 40} />;
}

type Props = {
  pageSize: number;
};

function CommunityFeaturedView({ pageSize }: Props) {
  const [hasMoreItems, setHasMoreItems] = useState<boolean>(true);

  function getUrl(
    pageIndex: number,
    previousPageData: CommunityGridItem[] | null
  ) {
    if (pageIndex === 0) {
      return getCommunityFeaturedUrl({ page: 0, limit: pageSize });
    }

    if (previousPageData === null || previousPageData.length === 0) {
      setHasMoreItems(false);
      return null;
    }

    return getCommunityFeaturedUrl({ page: pageIndex, limit: pageSize });
  }

  const {
    data: loadedGridItems,
    setSize,
    isValidating,
  } = useSWRInfinite(getUrl, cachedApiFetcher(gridItemHandler), {
    initialSize: 1,
    revalidateFirstPage: false,
  });

  function onScrollNearEnd() {
    setSize((size) => size + 1);
  }

  return (
    <div className={styles.Container}>
      <CommunityGrid
        gridItems={loadedGridItems?.flat()}
        hasMoreItems={hasMoreItems}
        isValidating={isValidating}
        onScrollNearEnd={onScrollNearEnd}
      />
    </div>
  );
}
